/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import TextField from '@TextField';
import { useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Popper from '@mui/material/Popper';
import Paper from '@mui/material/Paper';
import mappedQuestionIdIcons from '../../Data/Maps/mappedQuestionIdIcons';
import ValidationStatus from '../../Data/ValidationStatus';
import SagaStatus from '../../Data/SagaStatus';
import { getFirstEditableComponentOnPage } from '../../Helpers/components';
import { getErrorMessage } from '../../Helpers/validation';
import { validatePostcodeSaga } from '../../Lib/redux/sagas/ValidatePostcode/validatePostcode';
import {
  clearConstraintsValidations,
  setBrowserAutoFilledActive,
  setComponentValidationStatus,
  updateMaskedValue,
  updateValue,
} from '../../Lib/redux/slices/components';
import { setShouldNavigateForward } from '../../Lib/redux/slices/navigationControls';
import { setComponentIdToReceiveFocus } from '../../Lib/redux/slices/persistKeyboard';
import { mask } from '../../Utils/masking';
import QuestionText from '../QuestionText/QuestionText';
import {
  findComponentWithMatchingAdmiralId,
  getComponentValidationStatus,
  hasPostcodeConstraint,
  hasMappedPostcodeQuestionId,
  isEmailComponent,
} from '../__helpers__/component.helpers';
import { getAutoCompleteName, getHtmlInputType } from './textfield.helpers';
import makeTextFieldInputStyling from './useTextFieldInputStyles';
import {
  handleAnswerQuestionSideEffectsSaga,
  handleZipcodeUpdatedSaga,
} from '../../Hooks/actionCreators';
import { useAutoFocusFirstEditableComponent } from '../../Hooks/useAutoFocusFirstEditableComponent';
import { renderMustache } from '../../Helpers/mustache';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';
import { ComponentTypesToReceiveFocusOnPageEnter } from '../../Data/Components';

/**
 * Note there is no handling of phone number in this component as that is handled in PhoneNumberTextField
 */
const TextFieldInput = ({
  componentId,
  iFrameId,
  multiline,
  rows,
  text,
  subheading,
  value,
  formId,
}) => {
  const textfieldExperimentName = useGetExperimentName({
    componentId,
    componentName: 'TextField',
  });

  const { countryCode, isIos, isSafari, isAndroid } = useSelector(
    (store) => store.formConfig
  );
  const dispatch = useDispatch();
  const theme = useTheme();
  const styling = makeTextFieldInputStyling({ theme });
  const component = useSelector(
    (i) => i.components[`component_${componentId}`]
  );
  const components = useSelector((store) => store.components);
  const { pages } = useSelector((store) => store.paging);
  const { componentIdToReceiveFocus } = useSelector(
    (store) => store.persistKeyboard
  );
  const { componentIdToSagaStatus } = useSelector(
    (store) => store.dataMappings
  );
  const isPostcodeComponent = hasPostcodeConstraint({ component });
  const isComponentMappedToPostcodeQuestionId = hasMappedPostcodeQuestionId({
    component,
  });

  const isEmailComponentWithSuggestions =
    isEmailComponent({ component }) &&
    component.input?.autocompleteEmailDomains?.length > 0;

  const isCityAndStateAutofillEnabled =
    component.input?.triggerCityAndStateAutofill === true;

  const { mappedQuestionId, displayQuestionTextInsideTextfield } =
    component.component;
  const {
    autoCompleteType,
    placeholder,
    isAutoFilledActive,
    maskPattern,
    maskedValue,
  } = component.input;

  const errorMessage = getErrorMessage(component.validation?.constraints);
  const componentStatus = getComponentValidationStatus({ component });
  const isAnswered =
    componentStatus === ValidationStatus.VALID ||
    componentStatus === ValidationStatus.INVALID;
  const [isAutoFilled, setIsAutoFilled] = useState(false);
  const [attemptNavigate, setAttemptNavigate] = useState(false);
  const [showDomainSuggestions, setShowDomainSuggestions] = useState(false);
  const [emailPrefix, setEmailPrefix] = useState('');
  const [textFieldWidth, setTextFieldWidth] = useState(0);
  const [popperStyles, setPopperStyles] = useState({});
  const textFieldRef = useRef(null);
  const [filteredDomains, setFilteredDomains] = useState([]);
  const [isSelectingDomain, setIsSelectingDomain] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const isPostcodeRegex = component.validation?.constraints?.postcodeRegex;

  const dispatchValidation = (currentValue) => {
    if (isPostcodeComponent && currentValue !== undefined && !isPostcodeRegex) {
      batch(() => {
        dispatch(
          validatePostcodeSaga({
            countryCode,
            value: currentValue,
            componentId,
            iFrameId,
            formId,
          })
        );
      });
    } else {
      batch(() => {
        dispatch(
          setComponentValidationStatus({
            countryCode,
            componentId,
            iFrameId,
            formId,
          })
        );
        dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
      });
    }
  };

  const handleEmailSuggestions = (newValue) => {
    if (!isEmailComponentWithSuggestions) return;

    const atIndex = newValue.indexOf('@');
    if (atIndex <= 0) {
      setShowDomainSuggestions(false);
      return;
    }

    const prefix = newValue.slice(0, atIndex);
    const domainPart = newValue.slice(atIndex + 1).toLowerCase();
    setEmailPrefix(prefix);

    if (!domainPart) {
      setFilteredDomains(component.input?.autocompleteEmailDomains);
      setShowDomainSuggestions(true);
      return;
    }

    const exactMatch = component.input?.autocompleteEmailDomains?.some(
      (domain) => domain.toLowerCase() === domainPart
    );

    if (exactMatch) {
      setShowDomainSuggestions(false);
      return;
    }

    const matchingDomains = component.input?.autocompleteEmailDomains?.filter(
      (domain) => domain.toLowerCase().startsWith(domainPart)
    );

    setFilteredDomains(matchingDomains);
    setShowDomainSuggestions(matchingDomains.length > 0);
  };

  const updateSiblingPostcodeComponent = (newValue) => {
    if (!isComponentMappedToPostcodeQuestionId) return;

    const matchingComponent = findComponentWithMatchingAdmiralId(
      component,
      components
    );

    if (!matchingComponent) return;

    if (!newValue) {
      return;
    }

    dispatch(
      updateValue({
        componentId: matchingComponent.component.id,
        value: newValue,
      })
    );

    dispatch(
      validatePostcodeSaga({
        countryCode,
        value: newValue,
        componentId: matchingComponent.component.id,
        iFrameId,
        formId,
      })
    );
  };

  const handleChange = (params) => {
    batch(() => {
      if (isPostcodeComponent && maskPattern) {
        const newMaskedValue = mask({ value: params.value, maskPattern });
        dispatch(
          updateMaskedValue({ componentId, maskedValue: newMaskedValue })
        );
      }
      dispatch(updateValue({ componentId, value: params.value }));

      if (isAutoFilledActive) {
        dispatch(setBrowserAutoFilledActive({ componentId, value: false }));
      }

      handleEmailSuggestions(params.value);
    });
  };

  const handleDomainSelect = (domain) => {
    setIsSelectingDomain(true);
    const newValue = `${emailPrefix}@${domain}`;
    handleChange({ value: newValue });
    setShowDomainSuggestions(false);
    // Use setTimeout to ensure the blur handler doesn't fire before the value is updated
    setTimeout(() => {
      dispatchValidation(newValue);
      setIsSelectingDomain(false);
    }, 0);
  };

  const handleDomainMouseDown = (e) => {
    // Prevent the blur event from firing when clicking a email domain suggestion
    e.preventDefault();
  };

  const handleClick = () => {
    dispatch(
      clearConstraintsValidations({ componentId: component.component.id })
    );
  };

  const getTrimmedValueIfEmail = (valueToTrim) => {
    if (isEmailComponent({ component }) && typeof valueToTrim === 'string') {
      const trimmedValue = valueToTrim.trim();
      if (trimmedValue !== valueToTrim) {
        dispatch(updateValue({ componentId, value: trimmedValue }));
        return trimmedValue;
      }
    }
    return valueToTrim;
  };

  const handleKeyDown = (event) => {
    // Browser auto fill detection
    if (event.key === 'Unidentified' && event.code === undefined) {
      setIsAutoFilled(true);
      return;
    }
    setIsAutoFilled(false);

    const firstEditableComponentOnNextPage = getFirstEditableComponentOnPage(
      component.component.page + 1,
      components,
      pages
    );
    const valueToValidate = getTrimmedValueIfEmail(value);

    switch (event.key) {
      case 'Enter':
        // No actions if we're in a multiline textfield
        if (multiline === true) {
          event.stopPropagation();
          return;
        }

        // Only select the domain if we're in an opened email suggestion dropdown
        if (
          showDomainSuggestions &&
          filteredDomains.length > 0 &&
          selectedIndex >= 0
        ) {
          event.preventDefault();
          handleDomainSelect(filteredDomains[selectedIndex]);
          return;
        }

        // Handle regular Enter press by validating and attempting navigation
        if (
          (isIos || isAndroid) &&
          ComponentTypesToReceiveFocusOnPageEnter.includes(
            firstEditableComponentOnNextPage?.input?.type
          )
        ) {
          dispatch(
            setComponentIdToReceiveFocus({
              componentIdToReceiveFocus:
                firstEditableComponentOnNextPage.component.id,
            })
          );
        }

        if (isPostcodeComponent && maskedValue) {
          dispatchValidation(mask({ value: valueToValidate, maskPattern }));
        } else {
          dispatchValidation(valueToValidate);
        }

        updateSiblingPostcodeComponent(valueToValidate);
        setAttemptNavigate(true);
        break;

      case 'ArrowDown':
        if (showDomainSuggestions && filteredDomains.length > 0) {
          event.preventDefault();
          setSelectedIndex((prevIndex) => {
            const nextIndex = prevIndex + 1;
            return nextIndex >= filteredDomains.length ? 0 : nextIndex;
          });
        }
        break;

      case 'ArrowUp':
        if (showDomainSuggestions && filteredDomains.length > 0) {
          event.preventDefault();
          setSelectedIndex((prevIndex) => {
            const nextIndex = prevIndex - 1;
            return nextIndex < 0 ? filteredDomains.length - 1 : nextIndex;
          });
        }
        break;

      default:
        break;
    }
  };

  const sagaStatus = componentIdToSagaStatus?.[componentId];

  useEffect(() => {
    const shouldPreventNavigation =
      sagaStatus === SagaStatus.PENDING && sagaStatus !== undefined;

    if (
      componentStatus === ValidationStatus.VALID &&
      attemptNavigate &&
      !shouldPreventNavigation
    ) {
      setAttemptNavigate(false);
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
    if (
      componentStatus === ValidationStatus.INVALID &&
      attemptNavigate &&
      !shouldPreventNavigation
    ) {
      setAttemptNavigate(false);
    }
  }, [componentStatus, attemptNavigate, componentId, sagaStatus, dispatch]);

  const inputRef = useRef(null);
  useAutoFocusFirstEditableComponent({ componentId, inputRef });

  useEffect(() => {
    if (inputRef.current && componentIdToReceiveFocus === componentId) {
      inputRef.current.focus({ preventScroll: true });
      dispatch(
        setComponentIdToReceiveFocus({ componentIdToReceiveFocus: null })
      );
    }
  }, [componentIdToReceiveFocus]);

  useEffect(() => {
    if (textFieldRef.current) {
      const width = textFieldRef.current.offsetWidth;
      if (width > 0) {
        setTextFieldWidth(width);
      }
    }
  }, []);

  // Additional useEffect to update positioning when suggestions show
  useEffect(() => {
    if (showDomainSuggestions && textFieldRef.current) {
      const rect = textFieldRef.current.getBoundingClientRect();
      const inputRect = inputRef.current.getBoundingClientRect();

      // Calculate left position offset to align with the TextField
      const leftOffset = inputRect.left - rect.left;

      setPopperStyles({
        width: textFieldWidth,
        transform: `translate3d(${leftOffset}px, 0, 0)`,
      });
    }
  }, [showDomainSuggestions, textFieldWidth]);

  const handleBlur = () => {
    // Don't validate if we're in the process of selecting a domain
    if (isSelectingDomain) {
      return;
    }

    // Hide email domain suggestions when blurring (unless we're selecting a domain)
    setShowDomainSuggestions(false);

    if (value === undefined || value === '') {
      return;
    }

    const valueToValidate = getTrimmedValueIfEmail(value);

    if (isPostcodeComponent && maskedValue) {
      dispatchValidation(mask({ value: valueToValidate, maskPattern }));
    } else {
      dispatchValidation(valueToValidate);
    }

    updateSiblingPostcodeComponent(valueToValidate);
  };

  useEffect(() => {
    if (isAutoFilled) {
      // We usually trigger a validation on blur of the textfield but if it is filled by autofill then we will not see this blur. Therefore we will instead trigger the validation immediately.
      let valueToValidate = value;
      if (isEmailComponent({ component }) && typeof value === 'string') {
        const trimmedValue = value.trim();
        if (trimmedValue !== value) {
          dispatch(updateValue({ componentId, value: trimmedValue }));
          valueToValidate = trimmedValue;
        }
      }

      if (isPostcodeComponent && maskedValue) {
        dispatchValidation(mask({ value: valueToValidate, maskPattern }));
      } else {
        dispatchValidation(valueToValidate);
      }
      setIsAutoFilled(false);
    }
  }, [value]);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [filteredDomains]);

  useEffect(() => {
    const isValidatedSuccessfully = componentStatus === ValidationStatus.VALID;
    if (isCityAndStateAutofillEnabled && value && isValidatedSuccessfully) {
      dispatch(
        handleZipcodeUpdatedSaga({
          componentId,
          postcode: value,
          country: countryCode,
          formId,
          iFrameId,
        })
      );
    }
  }, [
    componentStatus,
    isCityAndStateAutofillEnabled,
    componentId,
    value,
    mappedQuestionId,
    countryCode,
    formId,
    iFrameId,
    dispatch,
    sagaStatus,
  ]);

  const displayedValue =
    isPostcodeComponent && maskPattern ? maskedValue : value || '';

  return (
    <Box css={styling.box} data-testid="textFieldContainer">
      {!displayQuestionTextInsideTextfield && (
        <QuestionText
          heading={renderMustache({
            template: text,
            components,
            currentComponent: component,
            iFrameId,
            formId,
          })}
          subheading={renderMustache({
            template: subheading,
            components,
            currentComponent: component,
            iFrameId,
            formId,
          })}
        />
      )}
      <Box css={styling.textFieldBox} ref={textFieldRef}>
        <TextField
          autoComplete={getAutoCompleteName(autoCompleteType, mappedQuestionId)}
          errorMessage={errorMessage}
          iconType={mappedQuestionIdIcons[mappedQuestionId]}
          id={`textField-${componentId}`}
          isAnswered={isAnswered}
          isAutoFilledActive={isAutoFilledActive}
          isSafari={isSafari}
          label={!displayQuestionTextInsideTextfield ? '' : text}
          multiline={multiline}
          value={displayedValue}
          onChange={handleChange}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          type={getHtmlInputType(
            component.input.fieldSubtype,
            isIos,
            isPostcodeComponent
          )}
          fullWidth
          placeholder={placeholder}
          rows={rows}
          shrinkLabel
          inputProps={{
            enterKeyHint: 'go',
            maxLength:
              isPostcodeComponent && maskPattern
                ? maskPattern.length
                : undefined,
          }}
          inputRef={inputRef}
          experimentName={textfieldExperimentName}
        />
        {isEmailComponentWithSuggestions && showDomainSuggestions && (
          <Popper
            open={showDomainSuggestions}
            anchorEl={textFieldRef.current}
            placement="bottom-start"
            css={styling.popper}
            style={{
              ...(textFieldWidth > 0 ? { width: `${textFieldWidth}px` } : {}),
              ...popperStyles,
            }}
            popperOptions={{
              strategy: 'absolute',
            }}
            modifiers={[
              {
                name: 'preventOverflow',
                enabled: true,
                options: {
                  altAxis: true,
                  boundary: 'viewport',
                  padding: 8,
                },
              },
              {
                name: 'offset',
                options: {
                  offset: [0, 2],
                },
              },
              {
                name: 'computeStyles',
                options: {
                  gpuAcceleration: false,
                },
              },
            ]}
          >
            <Paper elevation={3} css={styling.suggestionsPaper}>
              <List css={styling.suggestionsList}>
                {filteredDomains.map((domain, index) => (
                  <ListItem key={domain} disablePadding>
                    <ListItemButton
                      onClick={() => handleDomainSelect(domain)}
                      onMouseDown={handleDomainMouseDown}
                      selected={index === selectedIndex}
                      sx={{ py: 1 }}
                    >
                      {domain}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Popper>
        )}
      </Box>
    </Box>
  );
};

export default TextFieldInput;
